import { useState } from "react";
import axios from "axios";
import { axiosApiInstance } from "../../axios";
import { makeGetApiCall } from "../../shared/makeApiCall";
import { createManualExpense, updateExpenseRecord } from "../../shared/expenseApi";
import { buildExpenseListQuery } from "../../shared/expenseFilters";
import type { ExpenseListFilters } from "../../shared/expenseFilters";
import type { ApiErrorPayload, ApiMessage, ApiResult } from "../../types/api";
import type { ExpenseMutationPayload, ExpensePage, ExpenseRecord } from "../../types/expenses";

const emptyFilters: ExpenseListFilters = { startDate: null, endDate: null, userIds: [] };

const emptyExpenses: ExpensePage = {
  data: [],
  total: 0,
  current_page: 1,
  last_page: 1,
  per_page: 20,
};

const failure = (error: unknown): ApiResult<never> => ({
  success: false,
  data: axios.isAxiosError<ApiErrorPayload>(error) ? error.response?.data ?? {} : {},
});

export default function ExpensesListViewModel() {
  // follow paginator shape: { data: [], total: 0, current_page: 1 }
  const [expenses, setExpenses] = useState<ExpensePage>(emptyExpenses);
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  async function loadExpenses(page = 1, search: string | null = null, filters: ExpenseListFilters = {}) {
    setLoading(true);
    const query = buildExpenseListQuery(page, search, filters);
    const res = await makeGetApiCall<ExpensePage>(`expenses?${query}`);
    if (res.success) {
      setExpenses(res.data);
    }
    setLoading(false);
  }

  async function getExpense(expenseId: number) {
    return await makeGetApiCall<ExpenseRecord>(`expenses/${expenseId}`);
  }

  const resetFilters = () => {
    const f = { ...emptyFilters };
    setFilters(f);
    setAppliedFilters(f);
    setShowFilters(false);
  };

  const applyFilters = () => {
    setAppliedFilters({
      ...filters,
      userIds: [...(filters.userIds ?? [])],
    });
  };

  async function manualCreate(
    payloadOrFormData: ExpenseMutationPayload | FormData,
  ): Promise<ApiResult<ExpenseRecord>> {
    try {
      return { success: true, data: await createManualExpense(payloadOrFormData) };
    } catch (err: unknown) {
      return failure(err);
    }
  }

  async function updateExpense(
    expenseId: number,
    payload: ExpenseMutationPayload,
  ): Promise<ApiResult<ExpenseRecord>> {
    // The API validates editable expense fields and synchronizes any supplied items.
    try {
      return { success: true, data: await updateExpenseRecord(expenseId, payload) };
    } catch (err: unknown) {
      return failure(err);
    }
  }

  async function deleteExpense(expenseId: number): Promise<ApiResult<ApiMessage>> {
    try {
      const token = localStorage.getItem("authToken");
      const r = await axiosApiInstance.delete<ApiMessage>(`expenses/${expenseId}`, {
        headers: { Authorization: "Bearer " + token },
      });
      return { success: true, data: r.data };
    } catch (err: unknown) {
      return failure(err);
    }
  }

  return {
    expenses,
    getExpense,
    loadExpenses,
    manualCreate,
    showUpload,
    setShowUpload,
    setExpenses,
    loading,
    updateExpense,
    // filter state and helpers
    showFilters,
    setShowFilters,
    filters,
    appliedFilters,
    applyFilters,
    setFilters,
    resetFilters,
    deleteExpense,
  };
}
